import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, FileText, RefreshCw, UserRoundCheck, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AssignmentChangeModal } from '../components/AssignmentChangeModal'
import { Sidebar } from '../components/Sidebar'
import {
  buildHandoffDashboard,
  findCurrentAssignmentForLiver,
  type HandoffDashboardRow
} from '../lib/handoffDashboard'
import { hasInternalApi, internalApiGet } from '../lib/internalApi'
import { supabase } from '../lib/supabase'
import { useLivers } from '../lib/useLivers'
import type { Assignment } from '../lib/useAssignments'

type AssignmentsResponse = {
  assignments: Assignment[]
}

export function HandoffsPage() {
  const [assignmentTarget, setAssignmentTarget] = useState<HandoffDashboardRow | null>(null)
  const livers = useLivers()
  const assignments = useQuery<Assignment[]>({
    queryKey: ['assignments', 'all'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<AssignmentsResponse>('/api/assignments?limit=1000')
        return data.assignments
      }

      const { data, error } = await supabase
        .from('manager_assignments')
        .select(
          `*,
          manager:managers!manager_assignments_manager_id_fkey(id,name,email,department),
          predecessor:managers!manager_assignments_predecessor_manager_id_fkey(id,name,email)`
        )
        .order('started_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as Assignment[]
    }
  })
  const dashboard = buildHandoffDashboard(livers.data ?? [], assignments.data ?? [], new Date())
  const loading = livers.isLoading || assignments.isLoading
  const error = livers.error ?? assignments.error
  const currentAssignment = assignmentTarget
    ? findCurrentAssignmentForLiver(assignments.data ?? [], assignmentTarget.liverId)
    : null

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">引き継ぎ</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                現担当、未アサイン、直近の担当変更、引き継ぎノートを横断確認します。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">集計中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                引き継ぎ情報の取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
          </header>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <HandoffMetric icon={<Users className="h-4 w-4" />} label="対象ライバー" value={`${dashboard.activeLiverCount}名`} tone="slate" />
            <HandoffMetric icon={<UserRoundCheck className="h-4 w-4" />} label="現担当あり" value={`${dashboard.assignedCount}名`} tone="emerald" />
            <HandoffMetric icon={<AlertCircle className="h-4 w-4" />} label="未アサイン" value={`${dashboard.unassignedCount}名`} tone={dashboard.unassignedCount > 0 ? 'rose' : 'slate'} />
            <HandoffMetric icon={<RefreshCw className="h-4 w-4" />} label="30日以内の変更" value={`${dashboard.recentChangeCount}件`} tone="amber" />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">担当状況</h2>
                <p className="mt-1 text-xs text-gray-500">
                  各行から担当変更できます。漏れと最近の変更を確認します。
                </p>
              </div>
              <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                引継ぎノート {dashboard.notesCount}件
              </span>
            </div>
            {loading ? (
              <p className="px-4 py-8 text-sm text-gray-400">読み込み中...</p>
            ) : dashboard.rows.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {dashboard.rows.map((row) => (
                  <HandoffRow
                    key={row.liverId}
                    row={row}
                    onChangeAssignment={() => setAssignmentTarget(row)}
                  />
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-sm text-gray-400">対象ライバーがまだありません。</p>
            )}
          </section>
        </div>
      </main>
      {assignmentTarget && (
        <AssignmentChangeModal
          liverId={assignmentTarget.liverId}
          liverName={assignmentTarget.liverName}
          currentAssignment={currentAssignment}
          open={Boolean(assignmentTarget)}
          initialNotes={assignmentTarget.handoffNotes}
          onClose={() => setAssignmentTarget(null)}
        />
      )}
    </div>
  )
}

type MetricTone = 'slate' | 'emerald' | 'rose' | 'amber'

function HandoffMetric({
  icon,
  label,
  value,
  tone
}: {
  icon: React.ReactNode
  label: string
  value: string
  tone: MetricTone
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  )
}

function HandoffRow({
  row,
  onChangeAssignment
}: {
  row: HandoffDashboardRow
  onChangeAssignment: () => void
}) {
  return (
    <li className="grid gap-3 px-4 py-4 transition hover:bg-gray-50 lg:grid-cols-[minmax(0,1fr)_120px] lg:items-center">
      <Link
        to={`/liver/${row.liverId}`}
        className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(160px,0.8fr)_minmax(140px,0.6fr)_minmax(0,1fr)_80px] lg:items-center"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-gray-900">{row.liverName}</p>
            {row.state === 'unassigned' && (
              <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700">未アサイン</span>
            )}
            {row.isRecentChange && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">最近変更</span>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-gray-500">
            {row.changeReason ?? (row.state === 'unassigned' ? '担当者を設定してください' : '現担当')}
          </p>
        </div>

        <SmallField label="現担当" value={row.managerName ?? '-'} />
        <SmallField label="部署" value={row.managerDepartment ?? '-'} />
        <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <FileText className="h-3.5 w-3.5" />
            引き継ぎ
          </div>
          <p className="mt-0.5 line-clamp-2 text-sm text-gray-700">
            {row.handoffNotes ?? (row.predecessorName ? `前任: ${row.predecessorName}` : '-')}
          </p>
        </div>
        <span className="w-fit rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600">
          詳細
        </span>
      </Link>
      <button
        type="button"
        onClick={onChangeAssignment}
        className="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-gray-900 bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800 sm:w-fit lg:justify-self-end"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        {row.state === 'unassigned' ? 'アサイン' : '担当変更'}
      </button>
    </li>
  )
}

function SmallField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="mt-0.5 truncate text-sm text-gray-700">{value}</p>
    </div>
  )
}

const TONE_CLASS: Record<MetricTone, string> = {
  slate: 'bg-slate-100 text-slate-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  rose: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700'
}
